import { useLocalStorageState, useSessionStorageState } from 'ahooks';
import { AxiosError } from 'axios';
import { FirebaseError } from 'firebase/app';
import {
  browserLocalPersistence,
  browserSessionPersistence,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  getAdditionalUserInfo,
  getRedirectResult,
  GoogleAuthProvider,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithRedirect,
  User,
  UserCredential,
} from 'firebase/auth';
import { FormikHelpers } from 'formik';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toIntlLocale } from 'src/functions/date.functions';
import { resolveApiErrorMessage } from 'src/utils/resolveApiError';
import {
  PublicRoutes,
  RouterPaths,
} from '../../components/Router/Router.constants';
import { firebase } from '../../firebase/firebase.singleton';
import {
  ID_TOKEN_STORAGE_KEY,
  getLiveAuthState,
  purgePersistedSessionMirrors,
  readSessionOwner,
  resolveStaleSide,
  writeSessionOwner,
} from '../../firebase/authToken.service';
import { LLog, LOG_COMPONENTS, LOG_OPERATIONS } from '../../lib/logging';
import { sentry } from '../../sentry/sentry.singleton';
import { socketManager } from '../SocketsProvider/Socket.singleton.ts';
import { eAlertType } from '../ToastProvider/Toast.enum';
import { useToastContext } from '../ToastProvider/Toast.hook';
import { useAnalyticsAuth } from '../../hooks/analytics/auth/useAnalyticsAuth.hook';
import { AUTH_EVENTS } from '../../hooks/analytics/auth/authAnalytics.constants';
import {
  isTerminalAuthError,
  REJECTED_PASSWORD_CODES,
} from './User.authErrors';
import { eUserRole } from './User.enums';
import {
  findInvitationByEmail,
  readInvitationRefusal,
  tInvitationDoorOutcome,
  toInvitationDoorOutcome,
} from './User.invitation';
import {
  UserService,
  buildSessionTouchGate,
  getStoredUserId,
  getStoredWorkspaceId,
} from './User.service';
import {
  tAccountRecoveryForm,
  tAuthResponse,
  tSessionUser,
  tSignInFrom,
  tSignUp,
  tSignUpRequest,
  tUpdateNameAndPhoneProfile,
  tUserContext,
  tUserRoleSettlement,
} from './User.types';
import { composeFullName } from '../../functions/string.functions';

// How long a cross-account recovery waits for Sentry before navigating away.
// The report is diagnostic; a slow or blocked Sentry must not keep the user on
// a page that is authenticating as somebody else.
const SENTRY_FLUSH_TIMEOUT_MS = 1_000;

export default function useUser(): tUserContext {
  const { t, i18n } = useTranslation('settings/workspace');
  const { t: tErrors } = useTranslation('errors/errors');
  const { addAlert } = useToastContext();

  const localStorageStageOptions = {
    listenStorageChange: true,
    defaultValue: undefined,
  };

  const {
    identifyUser,
    resetAnalytics,
    startSessionRecording,
    stopSessionRecording,
    trackAuthEvent,
  } = useAnalyticsAuth();

  const [currentUserRole, setCurrentUserRole] = useState<eUserRole>(
    eUserRole.SELLER,
  );
  // Set by WorkspaceProvider when the role lookup settles. The role alone
  // cannot say it was looked up, since its default is a real role.
  const [userRoleSettlement, setUserRoleSettlement] = useState<
    tUserRoleSettlement | undefined
  >();

  // BUG-801: the token mirror is per-tab (sessionStorage), NOT localStorage.
  // The old profile-global key let a second account's login overwrite the
  // bearer every other tab was sending, while those tabs kept painting the
  // first user's UI. Axios no longer reads this mirror for regular requests
  // (it resolves the live Firebase token per request — see axios.singleton.ts);
  // the mirror only feeds keepalive/pagehide beacons and the transient-failure
  // fallback in firebase/authToken.service.ts.
  const [idToken, setIdToken] = useSessionStorageState<string | undefined>(
    ID_TOKEN_STORAGE_KEY,
    localStorageStageOptions,
  );

  const [currentUserId, setCurrentUserId] = useLocalStorageState<
    string | undefined
  >('currentUserId', localStorageStageOptions);

  const [currentUser, setCurrentUser] = useLocalStorageState<
    tSessionUser | undefined
  >('currentUser', localStorageStageOptions);

  const [hasSession, setHasSession] = useState<boolean>(
    currentUserId ? true : false,
  );

  const [googleLoggingIn, setGoogleLoggingIn] = useState<boolean>(false);

  // Guard for the identify effect below. Cleared on logout so a same-user
  // re-login (no page reload) re-identifies after posthog.reset() wiped the
  // previous identity — otherwise every post-relogin event loses its person.
  const lastIdentifiedKeyRef = useRef<string | null>(null);
  const emailSignInInFlight = useRef<Promise<tInvitationDoorOutcome | void> | null>(
    null,
  );

  // Tears down everything scoped to THIS tab and nothing else: the per-tab
  // token mirror (sessionStorage), the live socket, analytics recording. It
  // deliberately touches no localStorage key, so a tab that has to exit a
  // cross-account state cannot revert identity mirrors another tab legitimately
  // owns (BUG-801 — the ahooks setters below are localStorage-backed and
  // broadcast a StorageEvent to every other tab, which is exactly the harm).
  const teardownTabSession = useCallback(() => {
    setIdToken(undefined);
    setHasSession(false);
    socketManager.disconnect();
    stopSessionRecording();
    resetAnalytics();
    lastIdentifiedKeyRef.current = null;
    setUserRoleSettlement(undefined);
  }, [setIdToken, setHasSession, stopSessionRecording, resetAnalytics]);

  // Full sign-out teardown: this tab plus the profile-global identity mirrors.
  // The purge is folded in here (rather than left to each call site) because
  // only 3 of its 9 callers used to pair with it, so signing out of A and into
  // B carried A's currentWorkspaceId across as the backend's sign-in
  // attribution hint.
  const clearLocalStorageState = useCallback(() => {
    teardownTabSession();
    setCurrentUserId(undefined);
    setCurrentUser(undefined);
    purgePersistedSessionMirrors();
  }, [teardownTabSession, setCurrentUserId, setCurrentUser]);

  // Recovery runs at most once per tab: after it, this tab is on its way to
  // the sign-in page and further token events must not re-enter it (on an auth
  // route there is no redirect to end the sequence, so an unguarded recovery
  // would log and tear down on every refresh).
  const crossAccountRecoveredRef = useRef<boolean>(false);

  // BUG-801: recovery from a cross-account session — the persisted identity
  // mirrors belong to one user while this tab's live Firebase credential
  // belongs to another (two accounts sharing a browser profile).
  //
  // Which side is stale is decided by RECORDED RECENCY (resolveStaleSide: the
  // credential's Firebase auth_time vs the loginAt stamped when the mirrors
  // were written), never by where the credential happens to be stored. Storage
  // location and recency are independent: a tab can hold a durable credential
  // from an old login while another tab writes the mirrors with a brand-new
  // per-tab login, and the reverse happens just as easily.
  //
  // Neither branch signs Firebase out. The credential may be the legitimate
  // session of another tab (durable credentials are shared profile-wide, so a
  // signOut propagates through IndexedDB to every tab), or a Google redirect
  // result still being processed, or — during activation — the account the user
  // created seconds ago. Redirecting this tab to sign-in already stops it from
  // signing requests as the wrong user, which is the whole security goal;
  // destroying a credential to achieve it is never necessary.
  //
  // No redirect on auth routes — a reload there would interrupt
  // getRedirectResult/activation mid-flight.
  const recoverFromCrossAccountSession = useCallback(
    async (liveUid: string, persistedUserId: string) => {
      if (crossAccountRecoveredRef.current) return;
      crossAccountRecoveredRef.current = true;

      const { authTimeMs } = await getLiveAuthState();
      const staleSide = resolveStaleSide({
        liveAuthTimeMs: authTimeMs,
        persistedUserId,
        sessionOwner: readSessionOwner(),
      });

      LLog.error(
        {
          component: LOG_COMPONENTS.AUTH,
          method: 'session',
          operation: LOG_OPERATIONS.AUTH,
        },
        'Cross-account session detected: live Firebase UID does not match the persisted identity. Recovering.',
        new Error('CrossAccountSession'),
        { liveUid, persistedUserId, staleSide },
      );

      // The durable production signal for "is BUG-801 still happening, and
      // which side is stale". LLog stops at console.error, and the redirect
      // below kills the breadcrumb with the page — so capture and flush.
      sentry.captureException(new Error('CrossAccountSession'), {
        tags: { staleSide },
        extra: { liveUid, persistedUserId, authTimeMs },
      });

      if (staleSide === 'tab') {
        teardownTabSession();
      } else {
        clearLocalStorageState();
      }

      const onAuthRoute = PublicRoutes.some((path) =>
        window.location.pathname.startsWith(path),
      );
      if (!onAuthRoute) {
        // Bounded: the report is diagnostic, so a slow or unreachable Sentry
        // must never strand the user on a cross-account page.
        await sentry.flush(SENTRY_FLUSH_TIMEOUT_MS).catch(() => undefined);
        window.location.href = RouterPaths.signIn;
      }
    },
    [clearLocalStorageState, teardownTabSession],
  );

  // BUG-801 transitional back-compat — removal tracked by BUG-808, remove one
  // release after this ships. Tabs still running the pre-deploy bundle read the
  // profile-global localStorage.idToken on every request; letting it go stale
  // would 401 them mid-work, and the old bundle's 401 handler signs Firebase
  // out globally, killing this tab's session too.
  //
  // Gated on the key ALREADY existing, which is what bounds the exposure: only
  // a profile that ran the old bundle has it, and the first purge (any login,
  // logout or recovery) removes it for good — this effect never resurrects it.
  // Nothing in THIS bundle reads the key. While it does exist it is still a
  // profile-global last-writer-wins value, so two old-bundle tabs on different
  // accounts can still race it; that is the residual risk BUG-808 closes.
  useEffect(() => {
    if (!idToken) return;
    try {
      if (localStorage.getItem(ID_TOKEN_STORAGE_KEY) === null) return;
      localStorage.setItem(ID_TOKEN_STORAGE_KEY, JSON.stringify(idToken));
    } catch {
      // Storage unavailable — old-bundle tabs are already broken in that case.
    }
  }, [idToken]);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(
      firebase.auth,
      async (user: User | null) => {
        try {
          if (user) {
            // BUG-801: reconcile the live credential against the persisted
            // identity BEFORE mirroring its token. Every interactive auth flow
            // (the sign-in/sign-up paths here and completeActivation in
            // Activate.hook) clears the mirrors first, so a legitimate login
            // has no persisted id left to mismatch against by the time its
            // token event arrives.
            const persistedUserId = getStoredUserId();
            if (persistedUserId && persistedUserId !== user.uid) {
              await recoverFromCrossAccountSession(user.uid, persistedUserId);
              return;
            }

            const idToken = await user.getIdToken();
            setIdToken(idToken);
            setHasSession(true);
          } else {
            // Try to refresh the session instead of clearing local storage
            try {
              // Check if there's a current user in Firebase auth
              const currentUser = firebase.auth.currentUser;
              if (currentUser) {
                // Force token refresh
                const refreshedToken = await currentUser.getIdToken(true);
                setIdToken(refreshedToken);
                setHasSession(true);
              } else {
                // INT-1241: both the callback user and currentUser are null, so
                // Firebase has definitively resolved to "no credential" — this
                // is not a refresh blip. Drop our own mirror of the session too.
                // hasSession is seeded from the persisted currentUserId (see the
                // useState initializer above), so without this the app boots
                // "signed in" on a dead credential: it renders the previous
                // user's identity and keeps sending the stale idToken until an
                // API 401 bounces it. That is exactly what an unchecked
                // "Recordar mi usuario" produces once the sessionStorage
                // credential dies with the browser.
                clearLocalStorageState();
              }
            } catch (refreshError) {
              LLog.error(
                {
                  component: LOG_COMPONENTS.AUTH,
                  method: 'session',
                  operation: LOG_OPERATIONS.AUTH,
                },
                'Session refresh failed',
                refreshError instanceof Error
                  ? refreshError
                  : new Error(String(refreshError)),
              );
              // GRO-959: only sign out when the credential is genuinely rejected.
              // A network blip here used to reload the app and reset onboarding.
              if (isTerminalAuthError(refreshError)) {
                onSignOut();
              }
            }
          }
        } catch (error: unknown) {
          if (error instanceof FirebaseError) {
            // Handle specific Firebase errors
            switch (error.code) {
              case 'auth/invalid-user-token':
                // Token is invalid, user needs to sign in again
                LLog.error(
                  {
                    component: LOG_COMPONENTS.AUTH,
                    method: 'token',
                    operation: LOG_OPERATIONS.AUTH,
                  },
                  'Session expired. Please sign in again.',
                  error,
                );
                break;
              case 'auth/user-token-expired':
                // Try to refresh the token instead of requiring re-login
                LLog.info(
                  {
                    component: LOG_COMPONENTS.AUTH,
                    method: 'token',
                    operation: LOG_OPERATIONS.AUTH,
                  },
                  'User token expired, trying to refresh',
                );
                try {
                  const currentUser = firebase.auth.currentUser;
                  if (currentUser) {
                    // Force token refresh
                    const refreshedToken = await currentUser.getIdToken(true);
                    setIdToken(refreshedToken);
                    setHasSession(true);
                    LLog.info(
                      {
                        component: LOG_COMPONENTS.AUTH,
                        method: 'token',
                        operation: LOG_OPERATIONS.AUTH,
                      },
                      'Token successfully refreshed after expiration',
                    );
                  } else {
                    clearLocalStorageState();
                    LLog.error(
                      {
                        component: LOG_COMPONENTS.AUTH,
                        method: 'token',
                        operation: LOG_OPERATIONS.AUTH,
                      },
                      'Your session has expired. Please sign in again.',
                    );
                  }
                } catch (refreshError) {
                  LLog.error(
                    {
                      component: LOG_COMPONENTS.AUTH,
                      method: 'token',
                      operation: LOG_OPERATIONS.AUTH,
                    },
                    'Failed to refresh expired token',
                    refreshError instanceof Error
                      ? refreshError
                      : new Error(String(refreshError)),
                  );
                  // GRO-959: a failed refresh is only terminal when Firebase says
                  // the credential itself is bad; transient faults keep the session.
                  if (isTerminalAuthError(refreshError)) {
                    onSignOut();
                  }
                }
                break;
              case 'auth/network-request-failed':
                LLog.error(
                  {
                    component: LOG_COMPONENTS.AUTH,
                    method: 'network',
                    operation: LOG_OPERATIONS.AUTH,
                  },
                  'Network error. Please check your connection.',
                  error,
                );
                break;
              default:
                // GRO-959: this arm previously signed out on ANY unmapped
                // FirebaseError, including transient ones (`auth/too-many-requests`,
                // `auth/internal-error`, `auth/timeout`), which is what drove the
                // onboarding reload loop. Sign out only for codes that genuinely
                // invalidate the credential.
                if (isTerminalAuthError(error)) {
                  onSignOut();
                }
                LLog.error(
                  {
                    component: LOG_COMPONENTS.AUTH,
                    method: 'error',
                    operation: LOG_OPERATIONS.AUTH,
                  },
                  `Authentication error: ${error.message}`,
                  error,
                );
            }
          }
        }
      },
    );

    // A 10-minute setInterval that force-refreshed an expired token used to sit
    // here. It never ran (setInterval passes no arguments, so the callback's
    // `user` parameter was always undefined) and it was redundant twice over:
    // the SDK refreshes proactively before expiry and fires onIdTokenChanged,
    // and the axios interceptor's getIdToken() refreshes on demand per request.
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- U19 preserves the existing auth subscription trigger (KTD4).
  }, [clearLocalStorageState, recoverFromCrossAccountSession]);

  // BUG-801 (cross-tab arm): another tab's login rewrites the localStorage
  // identity mirrors WITHOUT firing a token event here — ahooks
  // (listenStorageChange) syncs currentUserId into this tab's state while the
  // live credential still belongs to the previous user. Catch that divergence
  // as soon as the synced value lands instead of waiting for the next token
  // refresh.
  useEffect(() => {
    const liveUid = firebase.auth.currentUser?.uid;
    if (liveUid && currentUserId && liveUid !== currentUserId) {
      void recoverFromCrossAccountSession(liveUid, currentUserId);
    }
  }, [currentUserId, recoverFromCrossAccountSession]);

  const setCurrentUserInfo = useCallback(
    (user: tAuthResponse) => {
      setCurrentUserId(user.data.authId);
      setCurrentUser(user.data);
      // BUG-801: stamp WHO wrote these mirrors and WHEN. This is the recency
      // record the cross-account reconciliation arbitrates on — without it the
      // guard would have to infer which session is newer, and every available
      // proxy for that (notably where Firebase parked the credential) is wrong
      // in at least one real scenario.
      writeSessionOwner(user.data.authId);
      setHasSession(true);
    },
    [setCurrentUser, setCurrentUserId],
  );

  // PostHog/analytics identify: fires when a known user's role lookup settles
  // for the current workspace, or at once for a user with no workspace yet,
  // and again when the role changes. Waiting keeps the default SELLER role out
  // of the person; a lookup that settled without a role identifies without
  // one. Guarded by uuid+workspace+role (ref declared above
  // clearLocalStorageState, which resets it on logout) — the `currentUser`
  // object identity changes on every refetch, and each redundant identify()
  // with the same distinct_id makes posthog-js emit a `$set` event (this was
  // ~2M events/mo of pure noise). The workspace is part of the key so identify
  // runs, and registers `user_role`, once per workspace whose role settled,
  // even when the role string is the same.
  useEffect(() => {
    if (!currentUser?.uuid) return;
    const hasWorkspace = !isEmpty(currentUser.workspaces);
    if (hasWorkspace && !userRoleSettlement) return;
    const role =
      hasWorkspace && userRoleSettlement?.isRoleResolved
        ? currentUserRole
        : undefined;
    const identifyKey = `${currentUser.uuid}:${userRoleSettlement?.workspaceId ?? ''}:${role ?? ''}`;
    if (lastIdentifiedKeyRef.current === identifyKey) return;
    lastIdentifiedKeyRef.current = identifyKey;
    identifyUser(currentUser, role);
    startSessionRecording();
  }, [
    currentUser,
    currentUserRole,
    userRoleSettlement,
    identifyUser,
    startSessionRecording,
  ]);

  // Boot session touch (U5): records a passive daily session row so returning
  // users on a persisted Firebase session are measurable — restoring the
  // login-days signal the 2026-04-02 AuthMiddleware change silently removed.
  // `onIdTokenChanged`'s single `if (user)` arm also fires on hourly token
  // refresh and right after interactive sign-in, so this is gated to fire at
  // most once per app load (ref) and once per user+workspace+UTC-day
  // (localStorage); the server-side dedup remains authoritative. Strictly
  // fire-and-forget: sessionTouch skips the 401 localStorage-wipe and Sentry
  // capture, so a deploy-skew 404/401 can never disrupt boot.
  const sessionTouchFiredRef = useRef<boolean>(false);
  useEffect(() => {
    const userUuid = currentUser?.uuid;
    if (!userUuid || sessionTouchFiredRef.current) return;
    sessionTouchFiredRef.current = true;

    const workspaceId = getStoredWorkspaceId();
    const { key, today } = buildSessionTouchGate(userUuid, workspaceId);

    try {
      if (localStorage.getItem(key) === today) return;
    } catch {
      // storage unavailable: fall through, server dedup still bounds it
    }

    void UserService.sessionTouch({ userUuid, workspaceId })
      .then(() => {
        try {
          localStorage.setItem(key, today);
        } catch {
          // ignore storage write failures
        }
      })
      .catch(() => {
        // fire-and-forget: a failed touch must never surface to the user
      });
  }, [currentUser]);

  /**
   * Resolves the Firebase credential into a backend identity. Returns true
   * only when the backend accepted the sign-in/sign-up and the user was set.
   * Sign-IN backend failures (403/5xx/network) are surfaced via alerts and
   * return false instead of throwing — the interactive-login callers MUST
   * check the result before emitting login analytics (INT-1243:
   * front.login.success used to fire on these swallowed failures). The
   * sign-up arm is true-or-throw, never false, so sign-up callers may
   * ignore the return value.
   *
   * BUG-800: the refusal for an invited address (428) is thrown from both
   * arms, never turned into an alert here. Each door reads it and tells its
   * page what to show.
   */
  const setUserInfo = useCallback(
    async (
      userCredential: UserCredential,
      options: {
        isSignUp?: boolean;
        firstName?: string;
        lastName?: string;
      } = {},
    ): Promise<boolean> => {
      const { isSignUp = false, firstName, lastName } = options;
      const firebaseUser = userCredential.user as User;
      if (isEmpty(firebaseUser)) {
        return Promise.reject(new Error('User not found'));
      }
      const idToken = await firebaseUser.getIdToken();
      setIdToken(idToken);

      let user: tAuthResponse | null = null;
      if (isSignUp) {
        const signUpRequest: tSignUpRequest = {
          firstName: firstName,
          lastName: lastName,
        };
        user = await UserService.signUp(signUpRequest);
        setCurrentUserInfo(user);
        return true;
      }

      try {
        user = await UserService.signIn();
        setCurrentUserInfo(user);
        return true;
      } catch (err) {
        if (readInvitationRefusal(err)) {
          throw err;
        }

        const error = err as AxiosError;
        const status = error.response?.status;

        switch (status) {
          case 409:
            addAlert({
              type: eAlertType.INFO,
              message: t('login-error-conflict'),
            });
            await UserService.signOut();
            user = await UserService.signIn();
            setCurrentUserInfo(user);
            return true;
          case 403:
            addAlert({
              type: eAlertType.ERROR,
              message: t('login-error-forbidden'),
            });
            return false;
          default:
            addAlert({
              type: eAlertType.ERROR,
              message: error.message || 'An unexpected error occurred.',
            });
            return false;
        }
      }
    },
    [addAlert, setCurrentUserInfo, setIdToken, t],
  );

  /**
   * Single emit point for the canonical interactive-login event (INT-1154:
   * front.login.success is THE login event — never add a second one). Only
   * call it after setUserInfo returned true. workspace_id is a best-effort
   * hint read from the app's own localStorage, because posthog.reset() at
   * submit start wiped the super-props. Caveat: the key is not cleared on
   * logout, so a cross-user re-login on a shared browser can carry the
   * previous user's workspace — accepted for a dashboard hint (unlike the
   * BFF session write, this path has no membership validation).
   */
  const trackLoginSuccess = useCallback(
    (method: 'email' | 'google') => {
      const workspaceHint = getStoredWorkspaceId();
      trackAuthEvent(AUTH_EVENTS.LOGIN_SUCCESS, {
        method,
        ...(workspaceHint ? { workspace_id: workspaceHint } : {}),
      });
    },
    [trackAuthEvent],
  );

  const signInWithEmail = useCallback(
    async (formData: tSignInFrom): Promise<tInvitationDoorOutcome | void> => {
      try {
        clearLocalStorageState();

        const { rememberMe, email, password } = formData;
        // INT-1241: the "Recordar mi usuario" checkbox was unbound, so this
        // always took the rememberMe branch — and the singleton then forced
        // sessionStorage on the next boot anyway. Now that the checkbox is
        // live, the unchecked branch must mean "forget me when I close the
        // browser" (browserSessionPersistence), NOT inMemoryPersistence, which
        // would log the user out on every page refresh.
        const persistence = rememberMe
          ? browserLocalPersistence
          : browserSessionPersistence;

        await setPersistence(firebase.auth, persistence);

        const result = await signInWithEmailAndPassword(
          firebase.auth,
          email,
          password,
        );

        const signedIn = await setUserInfo(result);

        // Interactive login truth event (GRO-841: the login click event was
        // removed and nothing marked a successful sign-in). Fired only for
        // explicit logins the backend actually accepted — setUserInfo swallows
        // backend 403/5xx into alerts and returns false (INT-1243); silent
        // session hydration is covered by $identify.
        if (signedIn) {
          trackLoginSuccess('email');
        }

        return Promise.resolve();
      } catch (error: unknown) {
        const refusal = readInvitationRefusal(error);
        if (refusal) {
          return toInvitationDoorOutcome(refusal, formData.email);
        }

        if (error instanceof FirebaseError) {
          // BUG-800: Firebase answers this door alone, and an invitee has no
          // account for it to accept. After a rejected password the email check
          // knows whether the typed address is invited; only when it is not, or
          // for any other code, does the code below show.
          const reason = REJECTED_PASSWORD_CODES.has(error.code)
            ? await findInvitationByEmail(formData.email)
            : null;
          if (reason) {
            return { kind: 'notice', reason, email: formData.email };
          }

          addAlert({
            type: eAlertType.ERROR,
            message: error?.code || 'Ha ocurrido un error',
          });
        }
        return Promise.reject(error);
      }
    },
    [addAlert, clearLocalStorageState, setUserInfo, trackLoginSuccess],
  );

  const onSignInWithEmailAndPasswordSubmit = useCallback(
    async (
      formData: tSignInFrom,
      props: FormikHelpers<tSignInFrom>,
    ): Promise<tInvitationDoorOutcome | void> => {
      const operation = emailSignInInFlight.current ?? signInWithEmail(formData);
      emailSignInInFlight.current = operation;

      try {
        return await operation;
      } finally {
        if (emailSignInInFlight.current === operation) {
          emailSignInInFlight.current = null;
        }
        props.setSubmitting(false);
      }
    },
    [signInWithEmail],
  );

  const onSignUpSubmit = useCallback(
    async (formData: tSignUp): Promise<tInvitationDoorOutcome | void> => {
      try {
        clearLocalStorageState();

        const { email, password, firstName, lastName } = formData;

        const result = await createUserWithEmailAndPassword(
          firebase.auth,
          email,
          password,
        );

        await setUserInfo(result, { isSignUp: true, firstName, lastName });
      } catch (error) {
        const refusal = readInvitationRefusal(error);
        if (refusal) {
          return toInvitationDoorOutcome(refusal, formData.email);
        }

        if (error instanceof FirebaseError) {
          return Promise.reject(error.code);
        }
        return Promise.reject(error);
      }
    },
    [clearLocalStorageState, setUserInfo],
  );

  const onSignUpWithGoogle =
    useCallback(async (): Promise<tInvitationDoorOutcome | void> => {
      let googleEmail = '';

      try {
        if (googleLoggingIn) {
          return;
        }

        setGoogleLoggingIn(true);

        const result = await getRedirectResult(firebase.auth);

        if (!result) return;

        googleEmail = result.user.email ?? '';

        clearLocalStorageState();

        const credential = GoogleAuthProvider.credentialFromResult(result);

        const additionalUserInfo = getAdditionalUserInfo(result);

        if (!credential) {
          addAlert({
            type: eAlertType.ERROR,
            message: 'El cliente no pudo ser autenticado',
          });
          return;
        }

        await setUserInfo(result, {
          isSignUp: true,
          firstName: additionalUserInfo?.profile?.given_name as string,
          lastName: additionalUserInfo?.profile?.family_name as string,
        });
      } catch (error) {
        const refusal = readInvitationRefusal(error);
        if (refusal) {
          return toInvitationDoorOutcome(refusal, googleEmail);
        }

        if (error instanceof FirebaseError) {
          return Promise.reject(error.code);
        }
        return Promise.reject(error);
      } finally {
        setGoogleLoggingIn(false);
      }
    }, [addAlert, clearLocalStorageState, googleLoggingIn, setUserInfo]);

  const onSignOut = useCallback(async (): Promise<void> => {
    try {
      // BUG-801: close the backend device session BEFORE signing Firebase out
      // — afterwards the axios interceptor has no live credential to sign the
      // request with and /auth/log-out is deterministically rejected. The call
      // is best-effort: a failed log-out must never strand the user logged in
      // locally, so failures are logged and the local teardown continues.
      await UserService.signOut().catch((error: unknown) => {
        LLog.error(
          {
            component: LOG_COMPONENTS.AUTH,
            method: 'signOut',
            operation: LOG_OPERATIONS.AUTH,
          },
          'Backend log-out failed; continuing local sign-out',
          error instanceof Error ? error : new Error(String(error)),
        );
      });
      await firebaseSignOut(firebase.auth);
      clearLocalStorageState();
      localStorage.removeItem('hasChangesLeadbot');
      localStorage.removeItem('hrefChangesLeadbot');
      window.location.href = RouterPaths.signIn;
    } catch (error) {
      return Promise.reject(error);
    }
  }, [clearLocalStorageState]);

  const onAccountRecoverySubmit = useCallback(
    async (form: tAccountRecoveryForm): Promise<void> => {
      try {
        const { email } = form;

        // GRO-610: without this, Firebase falls back to the project's
        // `defaultLocale` (English) regardless of the user's language, so the
        // reset email arrives unreadable and unbranded. Firebase Console
        // registers Auth email templates under base language codes (`es`,
        // `en`, `pt`), not region variants, so we take only the primary
        // subtag of `toIntlLocale`'s BCP-47 output (`es-MX` -> `es`) rather
        // than reusing it verbatim — that mapper targets `Intl`, which does
        // want the region. `auth` is a process-wide singleton and this value
        // is intentionally left set afterwards, so later Auth emails in the
        // session (e.g. a second recovery attempt) keep matching the user's
        // language.
        firebase.auth.languageCode = toIntlLocale(i18n.language).split('-')[0];
        await sendPasswordResetEmail(firebase.auth, email);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    [i18n.language],
  ); // });

  const retrieveGoogleSignInResult =
    useCallback(async (): Promise<tInvitationDoorOutcome | void> => {
      let googleEmail = '';

      try {
        if (googleLoggingIn) {
          return;
        }

        setGoogleLoggingIn(true);

        const result = await getRedirectResult(firebase.auth);

        if (!result) return;

        googleEmail = result.user.email ?? '';

        clearLocalStorageState();

        const credential = GoogleAuthProvider.credentialFromResult(result);

        const additionalUserInfo = getAdditionalUserInfo(result);
        if (!credential) {
          addAlert({
            type: eAlertType.ERROR,
            message: 'El cliente no pudo ser autenticado',
          });
          return;
        }

        if (additionalUserInfo?.isNewUser) {
          await setUserInfo(result, {
            isSignUp: true,
            firstName: additionalUserInfo.profile?.given_name as string,
            lastName: additionalUserInfo.profile?.family_name as string,
          });
        } else {
          // Same guard + hint semantics as the email path (INT-1243).
          const signedIn = await setUserInfo(result);
          if (signedIn) {
            trackLoginSuccess('google');
          }
        }
      } catch (error) {
        const refusal = readInvitationRefusal(error);
        if (refusal) {
          return toInvitationDoorOutcome(refusal, googleEmail);
        }

        addAlert({
          type: eAlertType.ERROR,
          message: `Ha ocurrido un error al iniciar sesión con Google: ${error}`,
        });
      } finally {
        setGoogleLoggingIn(false);
      }
    }, [
      addAlert,
      clearLocalStorageState,
      googleLoggingIn,
      setUserInfo,
      trackLoginSuccess,
    ]);

  const onSignInWithGoogle = useCallback(async () => {
    // INT-1241: set persistence explicitly instead of inheriting whatever the
    // Auth instance happens to hold. setPersistence mutates the shared
    // instance, so a failed email attempt with "Recordar mi usuario" unchecked
    // would otherwise leave it on SESSION and silently hand that policy to the
    // Google redirect that follows in the same page load. Google does not yet
    // carry the checkbox across the OAuth round trip (tracked separately), so
    // it commits to the durable default rather than to a leftover.
    await setPersistence(firebase.auth, browserLocalPersistence);
    await signInWithRedirect(firebase.auth, firebase.googleAuthProvider);
  }, []);

  const updateNameAndPhoneProfile = useCallback(
    async (values: tUpdateNameAndPhoneProfile) => {
      try {
        if (isEmpty(currentUser)) {
          return;
        }

        const updatedProfile = await UserService.updateUserNameAndPhone(
          values,
          currentUser.authId,
        );
        setCurrentUser({
          ...currentUser,
          name: composeFullName(
            updatedProfile.first_name,
            updatedProfile.last_name,
          ),
          firstName: updatedProfile.first_name,
          lastName: updatedProfile.last_name ?? '',
          phoneNumber: Number.parseInt(values.phoneNumber),
        });
      } catch (error) {
        // This is the toast the user actually sees: this function resolves rather
        // than rejecting, so a caller's .catch() never runs. Interpolating `error`
        // rendered "AxiosError: Request failed with status code 422" (BUG-449).
        addAlert({
          type: eAlertType.ERROR,
          message: resolveApiErrorMessage(error, tErrors),
        });
      }
    },
    [addAlert, currentUser, setCurrentUser, tErrors],
  );

  return useMemo(
    () => ({
      idToken,
      currentUserId,
      currentUser,
      setCurrentUser,
      hasSession,
      onSignInWithEmailAndPasswordSubmit,
      onSignUpSubmit,
      onSignOut,
      onAccountRecoverySubmit,
      currentUserRole,
      setCurrentUserRole,
      userRoleSettlement,
      setUserRoleSettlement,
      onSignInWithGoogle,
      googleLoggingIn,
      updateNameAndPhoneProfile,
      retrieveGoogleSignInResult,
      onSignUpWithGoogle,
    }),
    [
      currentUser,
      currentUserId,
      currentUserRole,
      googleLoggingIn,
      hasSession,
      idToken,
      onAccountRecoverySubmit,
      onSignInWithEmailAndPasswordSubmit,
      onSignInWithGoogle,
      onSignOut,
      onSignUpSubmit,
      onSignUpWithGoogle,
      retrieveGoogleSignInResult,
      setCurrentUser,
      updateNameAndPhoneProfile,
      userRoleSettlement,
    ],
  );
}
